import axios, { AxiosRequestConfig } from 'axios';
import { AppConfig, storageUtils, Logger } from '@oh-vcp/components-common';
import { doLogout, refreshToken } from './openidconnectService';

const logger = new Logger({ moduleName: 'apiService' });

let refreshTokenPromise: Promise<String> | null = null;
let hasRefreshFailed = false;

type RetryableRequestConfig = AxiosRequestConfig & {
  _retry?: boolean;
};

const apiService = axios.create({
  baseURL: AppConfig.apiHost,
  withCredentials: true,
  headers: {
    'Content-type': 'application/json',
    Accept: 'application/json'
  }
});

apiService.interceptors.request.use(
  (config: any) => {
    logger.info('intercept request: ', config);
    const token = storageUtils.getUserToken();
    if (token) {
      logger.info('PRIVATE');
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      logger.info('PUBLIC');
    }
    return config;
  },
  (error: any) => {
    const { response } = error;
    logger.info('intercept ERROR: ', response);
    return Promise.reject(error);
  }
);

apiService.interceptors.response.use(
  (response: any) => {
    logger.info('intercept response', response);
    return response;
  },
  async (error: any) => {
    const originalRequest = error.config as RetryableRequestConfig;
    logger.error('originalRequest: ', error.config);

    if (!originalRequest) {
      return Promise.reject(error);
    }

    if (error?.response?.status === 401) {
      logger.error('401: Unauthorized');
      return refreshTokenRetry(originalRequest);
    }

    // Handle 403: Forbidden
    if (error?.response?.status === 403) {
      // Navigate the user to a "Permission Denied" page or show a relevant message.
      logger.error('403 Permission Denied', error.config.url);
      const integrationType = sessionStorage.getItem('integration');
      if (!originalRequest.ignoreError && integrationType !== 'halo') {
        doLogout();
      }
      // const redirectUri = `${
      //   window.location.origin
      // }/logout?appState=403&originalUri=${encodeURIComponent(window.location.href)}`;
      // logger.info('intercept response redirectUri', redirectUri);
      // window.location.href = redirectUri;
      return Promise.resolve(true);
    }
    // Handle 404: Not Found
    // No special handling just reject it

    return Promise.reject(error);
  }
);

export const refreshTokenRetry = async (originalRequest: AxiosRequestConfig) => {
  logger.info('refreshTokenRetry invoked', originalRequest);

  const retryableRequest = originalRequest as RetryableRequestConfig;

  if (retryableRequest._retry) {
    logger.error('Request already retried once. Skipping token refresh retry.');
    return Promise.reject(new Error('Request already retried once.'));
  }

  if (hasRefreshFailed) {
    logger.error('Refresh previously failed. Skipping further refresh attempts.');
    return Promise.reject(new Error('Token refresh unavailable.'));
  }

  retryableRequest._retry = true;

  try {
    if (!refreshTokenPromise) {
      refreshTokenPromise = refreshToken()
        .then((newUserToken) => {
          apiService.defaults.headers.Authorization = `Bearer ${newUserToken}`;
          return newUserToken;
        })
        .catch((refreshError) => {
          hasRefreshFailed = true;
          throw refreshError;
        })
        .finally(() => {
          refreshTokenPromise = null;
        });
    }

    const newUserToken = await refreshTokenPromise;
    apiService.defaults.headers.Authorization = `Bearer ${newUserToken}`;

    const requestWithHeaders = retryableRequest as RetryableRequestConfig & {
      headers?: Record<string, string>;
    };
    if (!requestWithHeaders.headers) {
      requestWithHeaders.headers = {};
    }
    requestWithHeaders.headers.Authorization = `Bearer ${newUserToken}`;

    return await apiService(requestWithHeaders);
  } catch (refreshError) {
    logger.error('Token refresh failed. Blocking further refresh retries.');
    return Promise.reject(refreshError);
  }
};

export async function fetchList<T>(url: string): Promise<T[]> {
  const response = await apiService.get(url);
  logger.info('fetchList response: ', response.data);
  return response.data;
}

export async function apiGet<T>(
  url: string,
  params?: Record<string, any>,
  config?: AxiosRequestConfig | null
): Promise<T> {
  const response = await apiService.get(url, { ...config, params });
  logger.info('apiGet response: ', response.data);
  return response.data;
}

export async function apiPost<T>(
  url: string,
  data?: any,
  config?: AxiosRequestConfig | null
): Promise<T> {
  const response = await apiService.post(url, data, { ...config });
  logger.info('apiPost response: ', response.data);
  return response.data;
}

export async function apiDelete<T>(
  url: string,
  params?: Record<string, any>,
  config?: AxiosRequestConfig | null
): Promise<T> {
  const response = await apiService.delete(url, { ...config, params });
  logger.info('apiGet response: ', response.data);
  return response.data;
}

export async function apiGetRaw(
  url: string,
  params?: Record<string, any>,
  config?: AxiosRequestConfig | null
) {
  const response = await apiService.get(url, { ...config, params });
  logger.info('apiGetRaw response: ', response);
  return response;
}
export default apiService;
